import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { HandoffLink } from '@asko/shared';
import { ApiError, api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * MyID identifikatsiyasi — 2-ekran (BT §7.2).
 *
 * ── Nega QR asosiy yo'l ──────────────────────────────────────────────
 * Sotuvchining kompyuterida kamera odatda yo'q, MyID esa usiz ishlamaydi.
 * Shuning uchun ekranda QR chiqadi: mijoz uni O'Z telefonida ochadi,
 * o'sha yerda rasmga tushadi va sotuvchi ekrani o'zi davom etadi.
 *
 * Kamerasi bor qurilma uchun (planshet, noutbuk) shu yerda suratga
 * tushish imkoniyati ham qoldirilgan.
 *
 * Nega o'zimiz rasmga tushiramiz: MyID Inplace oqimida rasmni mijoz
 * tizimi yuklaydi. Bu bizga kerak — Alif mijoz rasmini majburiy talab
 * qiladi va uni bizning serverdan yuklab oladi.
 */

interface MyIdCaptureProps {
  identificationId: string;
  mode: 'mock' | 'real';
  submitting: boolean;
  onCancel: () => void;
  /**
   * Anketaga qaytish.
   *
   * Sotuvchi kartani yoki kontaktni xato kiritganini shu yerda ko'radi.
   * Hammasini boshidan boshlashning ma'nosi yo'q: identifikatsiya
   * hali tugamagan, anketa esa saqlanib turadi.
   */
  onBack: () => void;
}

export function MyIdCapture({
  identificationId,
  mode,
  submitting,
  onCancel,
  onBack,
}: MyIdCaptureProps) {
  const { t, apiMessage } = useI18n();
  const [link, setLink] = useState<HandoffLink | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<ApiError | null>(null);

  // Havolani ekran ochilishi bilan so'raymiz: mijoz kutib turmasin.
  useEffect(() => {
    let cancelled = false;

    async function ask() {
      try {
        const result = await api.post<HandoffLink>(`/myid/sessions/${identificationId}/handoff`);
        if (cancelled) return;
        setLink(result);
        setQr(
          await QRCode.toDataURL(result.url, {
            width: 320,
            margin: 1,
            errorCorrectionLevel: 'M',
          }),
        );
      } catch (err) {
        if (!cancelled) setLinkError(err instanceof ApiError ? err : null);
      }
    }

    void ask();
    return () => {
      cancelled = true;
    };
  }, [identificationId]);

  return (
    <div className="card">
      {mode === 'mock' && <span className="badge badge--warn">{t('myid.mockBadge')}</span>}

      <h2 className="section-title">{t('myid.title')}</h2>

      <p className="section-hint">{t('myid.qrHint')}</p>

      <div className="handoff">
        {qr ? (
          <img className="handoff__qr" src={qr} alt={t('myid.qrAlt')} />
        ) : linkError ? (
          <div className="alert alert--error" role="alert">
            {apiMessage(linkError)}
          </div>
        ) : (
          <div className="spinner" aria-label={t('common.loading')} />
        )}

        <ol className="handoff__steps">
          <li>{t('myid.qrStep1')}</li>
          <li>{t('myid.qrStep2')}</li>
          <li>{t('myid.qrStep3')}</li>
        </ol>
      </div>

      {link && <p className="handoff__url">{link.url}</p>}
      <p className="handoff__waiting">{t('myid.qrWaiting')}</p>

      <div className="flow-nav">
        <button className="btn" type="button" onClick={onBack} disabled={submitting}>
          {t('flow.back')}
        </button>
        <button className="btn" type="button" onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
